import { useEffect, useState } from "react";
import ApiResponse from "@core/types/apiResponse";
import { fetchDistrictsAsync } from "@features/district/services/districtSvc";
import { fetchChurchesAsync } from "@features/church/services/churchSvc";
import { SearchableOption } from "@core/components/SearchableListPicker";
import {
    SORT_BY_CHURCH_NAME,
    SORT_BY_DISTRICT_NAME,
    SORT_DIR_ASC,
} from "@core/constants/listSort";

interface PagedResponse {
    data?: any[];
    last_page?: number;
    meta?: { last_page?: number };
}

/**
 * Fetches every page of a paginated list endpoint and returns the flattened rows.
 * These endpoints return a flat Laravel paginator (`data`, `last_page` at top level),
 * though some are typed `{ data, meta }` — read `last_page` from either, default 1.
 */
async function fetchAllPaged(
    fetchPage: (page: number) => Promise<ApiResponse<PagedResponse>>
): Promise<any[]> {
    const first = await fetchPage(1);
    if (!first.isSuccess()) throw new Error("Failed to load list");
    const firstData = first.getData();
    const items: any[] = [...(firstData?.data ?? [])];
    const lastPage = firstData?.last_page ?? firstData?.meta?.last_page ?? 1;

    if (lastPage > 1) {
        const rest = await Promise.all(
            Array.from({ length: lastPage - 1 }, (_, i) => fetchPage(i + 2))
        );
        for (const r of rest) {
            if (r.isSuccess()) {
                const d = r.getData();
                if (d?.data) items.push(...d.data);
            }
        }
    }
    return items;
}

/**
 * Loads the entities one level below a parent, as picker options:
 * - parentApiScope "nation"   -> the nation's districts ({ value: district_id, label: district_name })
 * - parentApiScope "district" -> the district's churches ({ value: church_id, label: church_name })
 *
 * The underlying list endpoints already enforce hierarchical access server-side, so a
 * nation admin gets their nation's districts, a district admin gets their churches, etc.
 */
export function useChildEntities(
    parentApiScope: string,
    parentId: number | undefined,
    enabled: boolean,
    activeOnly = false
): { options: SearchableOption[]; loading: boolean; error: boolean } {
    const [options, setOptions] = useState<SearchableOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!enabled || !parentId) {
            setOptions([]);
            setError(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(false);
            try {
                let opts: SearchableOption[] = [];

                if (parentApiScope === "nation") {
                    // Fetch the caller's full (auth-scoped) district set with all:true — the
                    // proven call that returns every district — then narrow to this nation on
                    // the client. Tolerate a missing nation_id so a slim payload can never
                    // blank the dropdown.
                    const districts = await fetchAllPaged((page) =>
                        fetchDistrictsAsync({
                            all: true,
                            page,
                            sortBy: SORT_BY_DISTRICT_NAME,
                            sortDir: SORT_DIR_ASC,
                            ...(activeOnly && { isActive: true }),
                        })
                    );
                    const scoped = districts.filter(
                        (d) => d.nation_id == null || Number(d.nation_id) === Number(parentId)
                    );
                    opts = scoped.map((d) => ({ value: d.district_id, label: d.district_name }));
                } else if (parentApiScope === "district") {
                    // Same shape for churches: fetch all, scope to this district on the client,
                    // tolerating a missing district_id.
                    const churches = await fetchAllPaged((page) =>
                        fetchChurchesAsync({
                            all: true,
                            page,
                            sortBy: SORT_BY_CHURCH_NAME,
                            sortDir: SORT_DIR_ASC,
                            ...(activeOnly && { isActive: true }),
                        })
                    );
                    const scoped = churches.filter(
                        (c) => c.district_id == null || Number(c.district_id) === Number(parentId)
                    );
                    opts = scoped.map((c) => ({ value: c.church_id, label: c.church_name }));
                }

                if (!cancelled) setOptions(opts);
            } catch {
                if (!cancelled) {
                    setOptions([]);
                    setError(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [parentApiScope, parentId, enabled, activeOnly]);

    return { options, loading, error };
}
